"use client";

import { useState } from "react";
import clsx from "clsx";
import type { SustainabilityArticle as SustainabilityArticleData } from "@/lib/types";
import { BlurImage } from "@/components/ui/BlurImage";

/**
 * One sustainability case block (big.dk/sustainability):
 * aspect-video image → uppercase h2 → line-clamped body → "Read more+" that
 * expands fully and then disappears.
 */
export function SustainabilityArticle({
  article,
  priority = false,
}: {
  article: SustainabilityArticleData;
  priority?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <article className="mt-[40px] items-start px-7 md:mx-auto md:px-[70px] lg:max-w-[80vw] lg:px-[180px] xl:max-w-[70vw] 2xl:max-w-[62vw]">
      <div className="relative aspect-video h-auto w-full shrink-0">
        <BlurImage
          fill
          src={article.image.url}
          alt={article.image.alt || article.title}
          width={article.image.width}
          height={article.image.height}
          blurDataUrl={article.image.blurDataUrl}
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : "auto"}
          sizes="(max-width: 1024px) 100vw, 80vw"
          className="lg:col-start-2 lg:col-end-3 lg:row-span-3"
        />
      </div>
      <h2 className="mt-6 mb-5 text-sm text-[#1b1b1b] uppercase">{article.title}</h2>
      <p
        className={clsx(
          "text-sm whitespace-pre-wrap text-[#898989]",
          expanded
            ? "line-clamp-none max-h-[100%] md:line-clamp-none xl:line-clamp-none 2xl:line-clamp-none"
            : "line-clamp-8 overflow-hidden md:line-clamp-5 xl:line-clamp-7 2xl:line-clamp-8",
        )}
      >
        {article.body}
      </p>
      {!expanded ? (
        <button
          type="button"
          aria-label="read more"
          onClick={() => setExpanded(true)}
          className="mt-4 flex cursor-pointer items-center gap-2 text-xs text-[#898989] uppercase select-none"
        >
          Read more
          <span className="-mt-[2px] ml-1 inline-block origin-center transition-transform">
            +
          </span>
        </button>
      ) : null}
    </article>
  );
}
